import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import FormField from '../molecules/FormField';
import { Eye, EyeOff } from 'lucide-react';
import { sanitizeText } from '../../utils/sanitize';

/**
 * PasswordField Component - Forms
 * Password input with show/hide toggle
 * 
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.name - Input name
 * @param {string} props.error - Error message
 */
const PasswordField = React.forwardRef(
  (
    {
      label,
      name,
      error,
      helperText,
      required = false,
      className = '',
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    
    // Try to get form context (if used within react-hook-form)
    let formContext = null;
    try {
      formContext = useFormContext();
    } catch (e) {
      // Not within a form context
    }

    // Safely extract form methods and errors
    const register = formContext?.register;
    const formState = formContext?.formState;
    const errors = formState?.errors || {};

    // Get error from form context or external prop
    const fieldError = formContext && errors[name]
      ? errors[name]?.message 
      : error;

    // Ensure type is always set correctly and not overridden by props
    // Extract type from props to prevent override
    const { type: propsType, ...restProps } = props;
    
    // Determine the correct type based on showPassword state
    const passwordType = showPassword ? 'text' : 'password';
    
    return (
      <FormField
        ref={ref}
        label={label}
        name={name}
        error={fieldError}
        helperText={helperText}
        required={required}
        type={passwordType}
        className={className}
        sanitizeType="text"
        rightIcon={
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        }
        {...restProps}
      />
    );
  }
);

PasswordField.displayName = 'PasswordField';

export default PasswordField;
